import React from 'react';
import { useFormikContext } from 'formik';
import { PaperclipIcon, XIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { Document } from 'src/types/enums';
import { Dropzone } from '../../../../components/Dropzone';
import { Button } from '../../../../components/button/Button';
import { useDownloadDocument } from '../../../document/hooks/useDownloadDocument';
import { getDisplayError } from '../../../../utils/get-display-error';

interface ComplaintAttachmentUploadProps {}

interface FormValues {
  attachments: Document[];
}

export const ComplaintAttachmentUpload: React.FC<ComplaintAttachmentUploadProps> = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { downloadFile, downloadingFiles } = useDownloadDocument();

  const handleUpload = async (files: globalThis.File[]) => {
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.set('name', file.name);
        formData.set('file', file);

        const result = await fetch('/api/document/upload', {
          body: formData,
          method: 'post',
        });

        const text = await result.text();
        if (!result.ok) {
          console.error(text);
          throw new Error('Upload mislukt');
        }

        return JSON.parse(text).data;
      });

      const uploadedDocs = await Promise.all(uploadPromises);
      const newAttachments = [...values.attachments, ...uploadedDocs];
      setFieldValue('attachments', newAttachments);

      toast.success(`${uploadedDocs.length} bestand(en) geüpload`);
    } catch (err) {
      captureException(err);
      console.error('Failed to upload attachments', err);
      toast.error('Kon bijlagen niet uploaden: ' + getDisplayError(err));
    }
  };

  const handleRemove = (docId: string) => {
    const filteredAttachments = values.attachments.filter((doc) => doc.id !== docId);
    setFieldValue('attachments', filteredAttachments);
  };

  return (
    <div className="mb-4">
      <label className="label-text block mb-2">Bijlagen</label>

      {/* Upload Area */}
      <div className="mb-4">
        <Dropzone
          files={[]} // We don't use this for display since we handle it differently
          allowMultiple={true}
          onUpload={handleUpload}
          accept="application/pdf,.pdf,.png,.jpg,.jpeg"
        />
      </div>

      {/* Uploaded Attachments */}
      {values.attachments.length > 0 && (
        <div className="border border-border rounded-md p-3 mb-4">
          <div className="text-sm font-medium text-fg-secondary mb-2 flex items-center">
            <PaperclipIcon className="w-4 h-4 mr-1" />
            Geüploade bijlagen ({values.attachments.length})
          </div>

          <div className="space-y-2">
            {values.attachments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-surface-sunken rounded p-2">
                <div className="flex items-center flex-1">
                  <PaperclipIcon className="w-4 h-4 text-fg-muted mr-2" />
                  <span className="text-sm text-fg-secondary truncate">{doc.name}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    color="secondary"
                    onTrigger={() => downloadFile(doc)}
                    isLoading={downloadingFiles.includes(doc.id)}
                  >
                    Download
                  </Button>

                  <Button color="danger" onTrigger={() => handleRemove(doc.id)}>
                    <XIcon className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-fg-muted">Ondersteunde bestanden: PDF, PNG, JPG, JPEG</div>
    </div>
  );
};
